'use client';

import { useCallback } from 'react';

import { AppLink as Link } from '@/components/app-link';
import { Grip, LogIn, LogOut, Sparkles } from 'lucide-react';

import { Button } from '@naumu/ui/components/button';
import { useAnalytics } from '@naumu/analytics/analytics/analytics-context';
import { haptic } from '@naumu/ui/lib/haptics/haptics';
import { cn } from '@naumu/ui/utils/cn';

import {
	HEADER_SIGN_IN,
	HEADER_TRY,
	HEADER_TRY_HREF,
} from '../constants';
import { clearLandingSpacePinDismissal } from '@naumu/shared/spaces/landing-space-pin';
import { resolveCrossAppHref } from '@naumu/shared/routes/origins';
import { useGuestProvisioning } from '@naumu/platform-client/use-guest-provisioning';
import { useMarketingSession } from '@naumu/platform-client/use-marketing-session';

interface MarketingAuthActionsProps {
	/**
	 * `header` (default) - the inline cluster on the right of the sticky header.
	 * The primary action (Sign in for guests, Open <space> when authed) and "All
	 * spaces" stay visible on mobile; only Sign out collapses below md.
	 * `drawer` - stacked, full-width actions for the mobile nav drawer, where the
	 * full set is repeated (incl. Sign in / Sign out / All spaces standalone).
	 */
	layout?: 'header' | 'drawer';
}

/**
 * MarketingAuthActions - auth/CTA cluster shared across public pages.
 * Anonymous: Sign in (Google) + Try Naumu (guest provisioning, works from any
 * page). Authed: Sign out + All spaces + Open <space>. Mirrors the original
 * landing NavbarActions and reuses the existing `landing_cta_clicked` event.
 */
export const MarketingAuthActions = ({ layout = 'header' }: MarketingAuthActionsProps) => {
	const session = useMarketingSession();
	const analytics = useAnalytics();
	const { provisionAndGo, submitting } = useGuestProvisioning();

	const isDrawer = layout === 'drawer';

	const handleSignIn = useCallback(() => {
		haptic('medium');
		analytics.track('landing_cta_clicked', { section: 'navbar_sign_in' });
		// Route to the sign-in page (provider choice + email/password) instead of
		// kicking off Google OAuth directly.
		window.location.href = resolveCrossAppHref('/auth/sign-in?returnTo=/', 'marketing');
	}, [analytics]);

	// Get started is a REAL link to HEADER_TRY_HREF, and this is the JS that
	// intercepts it (W7 F3). Progressive enhancement, both directions: with JS
	// the click provisions a guest space instantly and never leaves the page;
	// without it - or on middle-click, cmd-click, "open in new tab", or a link
	// preview - the href is a genuine route into getting a space. Modified
	// clicks are left alone so the browser's own behaviour wins.
	const handleTry = useCallback(
		(e?: React.MouseEvent) => {
			if (e) {
				if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
					return;
				}
				e.preventDefault();
			}
			if (submitting) return;
			haptic('medium');
			analytics.track('landing_cta_clicked', { section: 'navbar_try_now' });
			provisionAndGo();
		},
		[analytics, provisionAndGo, submitting],
	);

	const handleLogout = useCallback(async () => {
		await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
		document.cookie = 'naumu-last-space=; path=/; max-age=0';
		clearLandingSpacePinDismissal();
		window.location.reload();
	}, []);

	// Loading - shimmer placeholder.
	if (session.loading) {
		return (
			<div
				className={cn(
					'animate-pulse rounded-[12px] bg-white/5',
					isDrawer ? 'h-11 w-full' : 'h-9 w-24',
				)}
			/>
		);
	}

	if (session.authenticated) {
		const space = session.space;

		if (isDrawer) {
			// Left-aligned, full-width touch rows (nav-drawer size). Every action carries a
			// leading icon except the primary "Open <space>" CTA.
			return (
				<>
					{space && (
						<Link href={`/spaces/${space.slug}/welcome`} className="w-full">
							<Button variant="solid" size="nav-drawer" type="button">
								<span className="block max-w-full truncate">
									Open {space.name}
								</span>
							</Button>
						</Link>
					)}
					{space && (
						<Link href="/home" className="w-full">
							<Button variant="ghost-muted" size="nav-drawer" type="button">
								<Grip />
								All spaces
							</Button>
						</Link>
					)}
					<Button
						variant="ghost-muted"
						size="nav-drawer"
						type="button"
						onClick={handleLogout}
					>
						<LogOut />
						Sign out
					</Button>
				</>
			);
		}

		return (
			<>
				<Button
					variant="ghost-muted"
					size="nav-sm"
					type="button"
					onClick={handleLogout}
					className="hidden md:inline-flex"
				>
					Sign out
				</Button>
				{space && (
					<>
						{/* "All spaces" stays beside the primary button on mobile too. */}
						<Link href="/home" className="shrink-0">
							<Button
								variant="ghost-muted"
								size="nav-sm"
								iconOnly
								type="button"
								aria-label="All spaces"
							>
								<Grip />
							</Button>
						</Link>
						<Link href={`/spaces/${space.slug}/welcome`} className="min-w-0">
							<Button variant="solid" size="nav-sm" type="button">
								{/* Cap + ellipsis so a long space name can't blow out the header. */}
								<span className="block max-w-[140px] truncate">
									Open {space.name}
								</span>
							</Button>
						</Link>
					</>
				)}
			</>
		);
	}

	// While provisioning is in flight the button keeps its exact footprint: the
	// DS `loading` prop hides the icon + label at opacity 0 (they still reserve
	// their width) and centres a spinner over them. `loading` disables the
	// button but keeps pointer-events, so a second click is swallowed here
	// instead of falling through to the wrapping <Link> mid-provision.

	if (isDrawer) {
		return (
			<>
				<Link href={HEADER_TRY_HREF} onClick={handleTry} className="w-full">
					<Button
						variant="solid-strong"
						size="nav-drawer"
						type="button"
						loading={submitting}
					>
						<Sparkles />
						{HEADER_TRY}
					</Button>
				</Link>
				<Button
					variant="ghost-muted"
					size="nav-drawer"
					type="button"
					onClick={handleSignIn}
				>
					<LogIn />
					{HEADER_SIGN_IN}
				</Button>
			</>
		);
	}

	return (
		<>
			<Button variant="ghost-muted" size="nav-sm" type="button" onClick={handleSignIn}>
				{HEADER_SIGN_IN}
			</Button>
			<Link href={HEADER_TRY_HREF} onClick={handleTry} className="shrink-0">
				<Button variant="solid-strong" size="nav-sm" type="button" loading={submitting}>
					{HEADER_TRY}
				</Button>
			</Link>
		</>
	);
};
